import React, { useState, useEffect } from 'react'
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Alert,
  Spinner,
  Badge,
  Modal
} from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faArrowLeft,
  faEdit,
  faTrash,
  faInfoCircle,
  faClock,
  faCalendarAlt,
  faUser,
  faTag,
  faHashtag,
  faLayerGroup,
  faPrint,
  faCheckCircle
} from '@fortawesome/free-solid-svg-icons'
import { useParams, useHistory } from 'react-router-dom'
import { connect } from 'react-redux'
import { withNamespaces } from 'react-i18next'
import {
  getKitchenDisplayDetails,
  deleteKitchenDisplay
} from '../Controller/KitchenDisplayController'
import Utility from '../../../../GlobalModules/Utility/Utility'
import '../Assets/css/kitchen-display.scss'

const ViewKitchenDisplay = (props) => {
  const { lng, slack } = useParams()
  const history = useHistory()

  const [kitchenDisplay, setKitchenDisplay] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState(false)

  useEffect(() => {
    fetchKitchenDisplayDetails()
  }, [slack])

  const fetchKitchenDisplayDetails = async () => {
    try {
      setLoading(true)
      const response = await getKitchenDisplayDetails(slack)

      if (response.success) {
        setKitchenDisplay(response.data)
      } else {
        Utility.toastNotifications(
          'Failed to load Kitchen Display details',
          'Error',
          'error'
        )
        setError('Failed to load Kitchen Display details')
      }
    } catch (err) {
      Utility.toastNotifications(
        'An error occurred while loading Kitchen Display details',
        'Error',
        'error'
      )
      setError('An error occurred while loading Kitchen Display details')
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = () => {
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (!kitchenDisplay) return

    setDeleteLoading(true)
    try {
      const response = await deleteKitchenDisplay(kitchenDisplay.slack)

      if (response.success) {
        Utility.toastNotifications(
          'Kitchen Display deleted successfully',
          'Success',
          'success'
        )
        // Redirect to list page after successful deletion
        history.push(`/${lng}/settings/kitchen-displays`)
      } else {
        Utility.toastNotifications(
          response.message || 'Failed to delete Kitchen Display',
          'Error',
          'error'
        )
        setShowDeleteModal(false)
      }
    } catch (err) {
      Utility.toastNotifications(
        'An error occurred while deleting Kitchen Display',
        'Error',
        'error'
      )
      setShowDeleteModal(false)
    } finally {
      setDeleteLoading(false)
    }
  }

  if (loading) {
    return (
      <Container fluid className='p-0'>
        <div
          className='d-flex justify-content-center align-items-center'
          style={{ minHeight: '400px' }}
        >
          <Spinner animation='border' variant='primary' />
          <span className='ms-2'>Loading Kitchen Display details...</span>
        </div>
      </Container>
    )
  }

  if (error) {
    return (
      <Container fluid className='p-0'>
        <Alert variant='danger'>{error}</Alert>
        <Button
          variant='outline-secondary'
          onClick={() => history.push(`/${lng}/settings/kitchen-displays`)}
        >
          <FontAwesomeIcon icon={faArrowLeft} className='me-2' />
          Back to List
        </Button>
      </Container>
    )
  }

  if (!kitchenDisplay) {
    return (
      <Container fluid className='p-0'>
        <Alert variant='warning'>Kitchen Display not found</Alert>
        <Button
          variant='outline-secondary'
          onClick={() => history.push(`/${lng}/settings/kitchen-displays`)}
        >
          <FontAwesomeIcon icon={faArrowLeft} className='me-2' />
          Back to List
        </Button>
      </Container>
    )
  }

  //======= add action permission ====//
  // Check multiple permissions at once
  const kitchenDisplayViewPermissions = Utility.hasMultipleActionPermissions(
    'settings',
    'kitchen_displays',
    ['view', 'create', 'edit', 'delete', 'export', 'approve', 'receive', 'verify', 'reject', 'verify_accept'],
    props?.userCredentials?.user_details?.user_type || ['admin', 'app_admin']
  );

  return (
    <Container fluid className='p-0 kitchen-display-view-page'>
      <Row className='top-header-box kitchen-display-view-header mb-3'>
        <Col className='kitchen-display-header-content'>
          <div className='kitchen-display-title-row'>
            <h4 className='kitchen-display-title'>
              {kitchenDisplay.label || 'Kitchen Display'}
            </h4>
            <Badge
              bg={kitchenDisplay.status == 1 ? 'success' : 'secondary'}
              className='badgeCustom'
            >
              {kitchenDisplay.status_data?.label ||
                (kitchenDisplay.status == 1 ? 'Active' : 'Inactive')}
            </Badge>
            {Boolean(kitchenDisplay.is_default) && (
              <Badge bg='primary' className='badgeCustom ms-2'>
                Default Kitchen Display
              </Badge>
            )}
          </div>
        </Col>
        <Col xs='auto' className='kitchen-display-header-actions'>
          {kitchenDisplayViewPermissions.edit && (
            <Button
              variant='outline-primary'
              onClick={() =>
                history.push(`/${lng}/settings/kitchen-displays/edit/${slack}`)
              }
            >
              <FontAwesomeIcon icon={faEdit} className='me-1' />
              Edit
            </Button>
          )}
          {kitchenDisplayViewPermissions.delete && (
            <Button variant='danger' onClick={handleDelete}>
              <FontAwesomeIcon icon={faTrash} className='me-1' />
              Delete
            </Button>
          )}
          <Button
            variant='outline-secondary'
            onClick={() => history.push(`/${lng}/settings/kitchen-displays`)}
          >
            <FontAwesomeIcon icon={faArrowLeft} className='me-1' />
            Back to List
          </Button>
        </Col>
      </Row>

      <Row>
        <Col>
          <Card className='custom-border-light mb-4 main-content-card kitchen-display-details-card'>
            <Card.Header>
              <h6>
                <FontAwesomeIcon icon={faInfoCircle} className='me-2' />
                Basic Information
              </h6>
            </Card.Header>
            <Card.Body>
              <div className='kitchen-display-details-grid'>
                <div className='detail-item'>
                  <span className='detail-icon'>
                    <FontAwesomeIcon icon={faHashtag} />
                  </span>
                  <div className='detail-content'>
                    <span className='detail-label'>Display Code</span>
                    <span className='detail-value'>
                      {kitchenDisplay.kitchen_display_code || '-'}
                    </span>
                  </div>
                </div>
                <div className='detail-item'>
                  <span className='detail-icon'>
                    <FontAwesomeIcon icon={faInfoCircle} />
                  </span>
                  <div className='detail-content'>
                    <span className='detail-label'>Name</span>
                    <span className='detail-value'>
                      {kitchenDisplay.label || '-'}
                    </span>
                  </div>
                </div>
                <div className='detail-item'>
                  <span className='detail-icon'>
                    <FontAwesomeIcon icon={faCheckCircle} />
                  </span>
                  <div className='detail-content'>
                    <span className='detail-label'>Default Display</span>
                    <span className='detail-value'>
                      {Boolean(kitchenDisplay.is_default) ? 'Yes (Primary Default)' : 'No'}
                    </span>
                  </div>
                </div>
                <div className='detail-item timer-orange'>
                  <span className='detail-icon timer-orange-icon'>
                    <FontAwesomeIcon icon={faClock} />
                  </span>
                  <div className='detail-content'>
                    <span className='detail-label'>Orange Timer</span>
                    <span className='detail-value timer-orange-value'>
                      {kitchenDisplay.orange_timer || '10.00'} min
                    </span>
                  </div>
                </div>
                <div className='detail-item timer-red'>
                  <span className='detail-icon timer-red-icon'>
                    <FontAwesomeIcon icon={faClock} />
                  </span>
                  <div className='detail-content'>
                    <span className='detail-label'>Red Timer</span>
                    <span className='detail-value timer-red-value'>
                      {kitchenDisplay.red_timer || '20.00'} min
                    </span>
                  </div>
                </div>
                <div className='detail-item'>
                  <span className='detail-icon'>
                    <FontAwesomeIcon icon={faCalendarAlt} />
                  </span>
                  <div className='detail-content'>
                    <span className='detail-label'>Created On</span>
                    <span className='detail-value'>
                      {Utility.get12HourFormat(kitchenDisplay.created_at) || '-'}
                    </span>
                  </div>
                </div>
                <div className='detail-item'>
                  <span className='detail-icon'>
                    <FontAwesomeIcon icon={faCalendarAlt} />
                  </span>
                  <div className='detail-content'>
                    <span className='detail-label'>Updated On</span>
                    <span className='detail-value'>
                      {Utility.get12HourFormat(kitchenDisplay.updated_at) || '-'}
                    </span>
                  </div>
                </div>
                <div className='detail-item'>
                  <span className='detail-icon'>
                    <FontAwesomeIcon icon={faUser} />
                  </span>
                  <div className='detail-content'>
                    <span className='detail-label'>Created By</span>
                    <span className='detail-value'>
                      {kitchenDisplay.created_by?.username || '-'}
                    </span>
                  </div>
                </div>
                <div className='detail-item'>
                  <span className='detail-icon'>
                    <FontAwesomeIcon icon={faUser} />
                  </span>
                  <div className='detail-content'>
                    <span className='detail-label'>Updated By</span>
                    <span className='detail-value'>
                      {kitchenDisplay.updated_by?.username || '-'}
                    </span>
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>

          {kitchenDisplay.categories && kitchenDisplay.categories.length > 0 && (
            <Card className='custom-border-light mb-4 main-content-card'>
              <Card.Header>
                <h6>
                  <FontAwesomeIcon icon={faLayerGroup} className='me-2' />
                  Assigned Categories
                </h6>
              </Card.Header>
              <Card.Body>
                <div className='d-flex flex-wrap gap-2'>
                  {kitchenDisplay.categories.map((category, index) => (
                    <Badge
                      key={index}
                      bg='info'
                      className='px-3 py-2'
                      style={{ fontSize: '0.8rem' }}
                    >
                      <FontAwesomeIcon icon={faTag} className='me-1' style={{ fontSize: '0.65rem' }} />
                      {typeof category === 'object' ? category.name : `Category ${category}`}
                    </Badge>
                  ))}
                </div>
              </Card.Body>
            </Card>
          )}
        </Col>
      </Row>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this Kitchen Display? This action
          cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button
            variant='danger'
            onClick={confirmDelete}
            disabled={deleteLoading}
          >
            {deleteLoading ? (
              <>
                <Spinner
                  as='span'
                  animation='border'
                  size='sm'
                  role='status'
                  aria-hidden='true'
                  className='me-2'
                />
                Deleting...
              </>
            ) : (
              'Delete'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  )
}

const mapStateToProps = (globalState) => {
  return {
    userCredentials: globalState.LoginReducer.userCredentials,
    token: globalState.LoginReducer.token,
    userEntryType: globalState.LoginReducer.userEntryType
  }
}

export default connect(mapStateToProps)(withNamespaces()(ViewKitchenDisplay))
